import * as _ from "lodash";
import * as Redux from "redux";

import * as FetchActions from "./FetchActions";
import * as SignalRActions from "./SignalRActions";
import * as AppPath from "../model/AppPath";

import { AgentGroup, defaultAgentGroupName } from "../../shared/model/AgentGroup";
import { ProcessWithStatus } from "../model/ProcessWithStatus";
import { getAgentGroup, getCurrentAgentGroup, State } from "../model/State";

import { ThunkResult, Utilities } from "../../shared/utilities";

import { HashMap } from "../../shared/model/HashMap";
import { ConfirmDeleteDialogState, IProcessIdWithName, IWarning } from "../model/ConfirmDeleteDialogState";
import { getProcessesWithProcessReferences, ProcessWithProcessReferences } from "../model/ProcessListSelectors";
import { ProcessVersionDetails } from "../model/ProcessVersion";

import { toggleVisibleColumns } from "../reducers/ListStateReducer";
import { FetchId, fetchSubprocessUsageData } from "./FetchActions";
import download from "downloadjs";
import JSONbig from "json-bigint";
import { Process } from "processList/model/Process";
import { maxVisibleColumns } from "processList/model/VisibleColumnsStore";
import { receiveProcessVariables } from "../../processEditor/actions/ProcessVariableActions";
import * as ProcessActions from "./ProcessActions";
import { ProcessTemplate } from "processTemplates/model/ProcessTemplate";
import { TemplateModalProps } from "processList/model/ListState";

export const CHANGE_AGENT_GROUP = "CHANGE_AGENT_GROUP";
export const START_LIST_ACTION = "START_LIST_ACTION";
export const FINISH_LIST_ACTION = "FINISH_LIST_ACTION";


export function startListAction(actionId: FetchId): FetchActions.FetchAction {
    return {
        type: START_LIST_ACTION,
        actionId: actionId
    };
}

export function finishListAction(actionId: FetchId): FetchActions.FetchAction {
    return {
        type: FINISH_LIST_ACTION,
        actionId: actionId
    };
}

export function changeAgentGroup(newAgentGroupName: string | null): ThunkResult<void, State> {
    return (dispatch, getState) => {
        const state = getState();
        const agentGroups = state.agentGroups;
        const agentGroupNames = Object.keys(agentGroups);
        if (agentGroupNames.length > 0) {
            // only check for valid groups if you have some set
            if (newAgentGroupName !== null && agentGroupNames.indexOf(newAgentGroupName) < 0) {
                // tslint:disable-next-line:no-console
                console.warn(`Could not find given agent group '${newAgentGroupName}'. Using the defaults.`);
                newAgentGroupName = "Default";
            }
        }
        const newAgentGroup = newAgentGroupName !== null && agentGroups[newAgentGroupName];
        if (!newAgentGroup) {
            Utilities.toastError(`Cannot find agent group data for group '${newAgentGroupName}`);
        }

        const agentGroup = (newAgentGroup as AgentGroup);

        dispatch<ChangeAgentGroupAction>({
            type: CHANGE_AGENT_GROUP,
            agentGroupName: agentGroup.name
        });

        const selectedAgentGroup = getAgentGroup(state, agentGroup.name);
        AppPath.setAgentGroupToPath(selectedAgentGroup.environment, selectedAgentGroup.name);

        dispatch(FetchActions.fetchProcessAndCountUpdate(agentGroup.environment, agentGroup.name));
    };
}

export interface ChangeAgentGroupAction extends Redux.Action {
    agentGroupName: string;
}

export function openConfirmDeleteProcessesDialog(ids: number[]): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        let state = getState();
        const currentAgentGroup = getCurrentAgentGroup(state);

        if (_.isEmpty(state.usedInProcessesMap) && state.isSubprocessList) {
            await dispatch(FetchActions.fetchSubprocessUsageData(currentAgentGroup.environment, currentAgentGroup.name));
            // The state has been changed here. Redux is syncronous and the store should always be updated before the next statement is reached.
            state = getState();
        }

        const selectedProcesses = _.intersectionWith(getProcessesWithProcessReferences(state), ids, (p: ProcessWithProcessReferences, id: number) => p.id === id);
        const processVersionDetails = await FetchActions.fetchProcessVersionDetails(selectedProcesses.map((p) => p.uniqueIdentifier));

        const deletModalWarnings = generateDeleteModalWarnings(selectedProcesses, processVersionDetails, state, currentAgentGroup);
        const processIdsWithNames: IProcessIdWithName[] = selectedProcesses.map((process) => ({ id: process.id, name: process.name }));
        const action: IOpenConfirmDeleteDialogAction = {
            type: OPEN_CONFIRM_DELETE_DIALOG,
            dialogState: { processesToDelete: processIdsWithNames, warnings: deletModalWarnings.warnings, showConfirmDelete: true, modalWarningLabel: deletModalWarnings.warningLabel, blockDelete: deletModalWarnings.blockDelete }
        };
        dispatch(action);
    };
}

export function generateDeleteModalWarnings(selectedProcesses: ProcessWithProcessReferences[], processVersionDetails: HashMap<ProcessVersionDetails[]>, state: State, currentAgentGroup: AgentGroup) {
    const warnings: IWarning = {};
    let warningLabel = "";
    let blockDelete = false;
    selectedProcesses.forEach((process) => {
        if (state.isSubprocessList && process.usedByProcessesList && process.usedByProcessesList.length) {
            // slice to only take 10 first processes
            process.usedByProcessesList.slice(0, 10).forEach((processReference) => {
                processReference.forEach((singeleReference) => {
                    _.defaultsDeep(warnings, {
                        [process.name]:
                        {
                            [singeleReference.processName + " - " + singeleReference.processVersion]:
                                [singeleReference.agentGroupWithDisplayName.map((ag) => ag.agentGroupDisplayName).join(", ")]
                        }
                    });
                });
            });
            warningLabel = FRENDS.Localizations.Warning_CanNotDeleteSubprocessInUse;
            blockDelete = true;
        } else {
            if (defaultAgentGroupName === currentAgentGroup.name) {
                warningLabel = FRENDS.Localizations.Process_DeleteCascadeMessage;

                processVersionDetails[process.uniqueIdentifier].forEach((version) => {
                    const deployedAgentGroups: string[] = [];
                    version.deployedToAgentGroups.forEach((deployed) => {
                        if (deployed !== defaultAgentGroupName) {
                            const agentGroup = getAgentGroup(state, deployed);
                            deployedAgentGroups.push(`${agentGroup.displayName} (${agentGroup.environmentDisplayName})`);
                        }
                    });
                    if (deployedAgentGroups.length) {
                        _.defaultsDeep(warnings, { [process.name]: { [`${version.majorVersion}.${version.minorVersion}.${version.version}`]: deployedAgentGroups } });
                    }
                });
            }
        }
    });
    return { warningLabel, blockDelete, warnings };
}

export function closeConfirmDeletDialog(): Redux.Action {
    return { type: CLOSE_CONFIRM_DELETE_DIALOG };
}

export function deleteProcesses(ids: number[]): ThunkResult<void, State> {
    return (dispatch, getState) => {
        dispatch(startListAction(FetchId.delete));
        dispatch(closeConfirmDeletDialog());
        const state = getState();
        const currentAgentGroup = getCurrentAgentGroup(state);
        return Utilities.postJsonAndParseResponse(`api/processList/delete/${currentAgentGroup.environment}/${currentAgentGroup.name}`, ids)
            .then(() => {
                dispatch(finishListAction(FetchId.delete));
                Utilities.toastSuccess(FRENDS.Localizations.Flash_DeleteSuccess);
                dispatch(FetchActions.fetchProcesses());
                if (state.drafts.length) {
                    dispatch(FetchActions.fetchDrafts());
                }
            }).catch((error) => {
                dispatch(FetchActions.reportFetchError(error));
                dispatch(FetchActions.fetchProcesses());
            });
    };
}

export function copyProcesses(ids: number[]): ThunkResult<void, State> {
    return (dispatch) => {
        dispatch(startListAction(FetchId.copy));
        return Utilities.postJsonAndParseResponse("api/processList/copy/", ids)
            .then(() => {
                dispatch(finishListAction(FetchId.copy));
                Utilities.toastSuccess(FRENDS.Localizations.Flash_CopySuccess);
                dispatch(FetchActions.fetchProcesses());
            })
            .catch((error) => {
                dispatch(FetchActions.reportFetchError(error));
                dispatch(FetchActions.fetchProcesses());
            });
    };
}

export function createNewProcessVersions(ids: number[]): ThunkResult<void, State> {
    return async (dispatch) => {
        try {
            dispatch(startListAction(FetchId.rebuild));
            await Utilities.postJsonAndParseResponse("api/processList/rebuild/", ids);
            dispatch(finishListAction(FetchId.rebuild));
            Utilities.toastSuccess(FRENDS.Localizations.Successful);
            return dispatch(FetchActions.fetchProcesses());
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
            return dispatch(FetchActions.fetchProcesses());
        }
    };
}

export function discardDraft(id: number): ThunkResult<void, State> {
    return (dispatch) => {
        dispatch(startListAction(FetchId.discardDraft));
        return Utilities.postJsonAndParseResponse("api/process/discard/" + id, {})
            .then(() => {
                dispatch(finishListAction(FetchId.discardDraft));
                Utilities.toastSuccess(FRENDS.Localizations.Flash_DiscardSucceeded);
                return dispatch(FetchActions.fetchDrafts());
            })
            .catch((error) => {
                dispatch(FetchActions.reportFetchError(error));
                return dispatch(FetchActions.fetchProcesses());
            });
    };
}


export const changedPinnedElementsKey = "changedPinnedElements";
export function pinProcesses(ids: number[]): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        try {
            const processGuids = getProcessesGuids(ids, getState().processes)
            dispatch(startListAction(FetchId.pinProcesses));
            await Utilities.putJsonAndParseResponse("api/user/config/pinProcesses/", { [changedPinnedElementsKey]: processGuids });
            dispatch(finishListAction(FetchId.pinProcesses));
            Utilities.toastSuccess(FRENDS.Localizations.Successful);
            return dispatch(FetchActions.fetchProcesses());
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
            return dispatch(FetchActions.fetchProcesses());
        }
    };
}


export const OPEN_CHANGELOG_MODAL = "OPEN_CHANGELOG_MODAL";
export interface OpenChangeLogAction extends Redux.Action {
    processId: number;
}

export function openChangeLogModal(id: number): OpenChangeLogAction {
    return {
        type: OPEN_CHANGELOG_MODAL,
        processId: id
    };
}

export const CLOSE_CHANGELOG_MODAL = "CLOSE_CHANGELOG_MODAL";
export function closeChangeLogModal(): Redux.Action {
    return {
        type: CLOSE_CHANGELOG_MODAL
    };
}

export const OPEN_TAG_MODAL = "OPEN_TAG_MODAL";
export interface OpenTagModalAction extends Redux.Action {
    processIds: number[];
}

export function openTagModal(ids: number[]): OpenTagModalAction {
    return {
        type: OPEN_TAG_MODAL,
        processIds: ids
    };
}

export const CLOSE_TAG_MODAL = "CLOSE_TAG_MODAL";
export function closeTagModal(): Redux.Action {
    return {
        type: CLOSE_TAG_MODAL
    };
}

export const ADD_TAGS = "ADD_TAGS";
export interface AddTagsAction extends Redux.Action {
    tags: string[];
}

export function addTags(tags: string[]): AddTagsAction {
    return {
        type: ADD_TAGS,
        tags: tags
    };
}

export function tagProcesses(processIds: number[], tags: string[], overwrite: boolean): ThunkResult<void, State> {
    return (dispatch, getState) => {
        const state = getState();
        const currentAgentGroupName = state.currentAgentGroupName;
        const currentEnvironmentName = state.agentGroups[currentAgentGroupName].environment;
        const processGuids = processIds.map((id) => {
            const proc = state.processes.find((p) => p.id === id);
            if (proc) {
                return proc.uniqueIdentifier;
            }
        });

        dispatch(addTags(tags));
        dispatch(startListAction(FetchId.tagProcesses));
        return Utilities.postJsonAndParseResponse("api/processList/tag", {
            processIds: processIds,
            tags: tags,
            overwrite: overwrite
        })
            .then(() => {
                dispatch(finishListAction(FetchId.tagProcesses));
                Utilities.toastSuccess(FRENDS.Localizations.ProcessTagsUpdated);
                // trigger fetch for the updated processes
                // TODO: Should we just add/overwrite the tags in a custom reducer action?
                dispatch(SignalRActions.updateProcesses("Updated", currentEnvironmentName, currentAgentGroupName, processGuids as string[]));
                dispatch(FetchActions.fetchAvailableTags())
                return dispatch(closeTagModal());
            })
            .catch((error) => dispatch(FetchActions.reportFetchError(error)));
    };
}

export function activate(ids: number[], agentGroupName: string): ThunkResult<void, State> {
    return (dispatch, getState) => {
        dispatch(startListAction(FetchId.activate));
        const state = getState();
        const currentAgentGroup = getAgentGroup(state, agentGroupName);
        return Utilities.postJsonAndParseResponse(`api/processList/activate/${currentAgentGroup.environment}/${currentAgentGroup.name}`, ids)
            .then(() => {
                dispatch(finishListAction(FetchId.activate));
                Utilities.toastSuccess(FRENDS.Localizations.ProcessActivateMessagesSentSuccessfully);
                return dispatch(FetchActions.fetchProcesses());
            })
            .catch((error) => dispatch(FetchActions.reportFetchError(error)));
    };
}

export function deactivate(ids: number[], agentGroupName: string): ThunkResult<void, State> {
    return (dispatch, getState) => {
        dispatch(startListAction(FetchId.deactivate));
        const state = getState();
        const currentAgentGroup = getAgentGroup(state, agentGroupName);
        return Utilities.postJsonAndParseResponse(`api/processList/deactivate/${currentAgentGroup.environment}/${currentAgentGroup.name}`, ids)
            .then(() => {
                dispatch(finishListAction(FetchId.deactivate));
                Utilities.toastSuccess(FRENDS.Localizations.ProcessDeactivateMessagesSentSuccessfully);
                return dispatch(FetchActions.fetchProcesses());
            })
            .catch((error) => dispatch(FetchActions.reportFetchError(error)));
    };
}


export function exportProcesses(ids: number[], agentGroupName: string): ThunkResult<void, State> {
    return async (dispatch) => {
        try {
            dispatch(startListAction(FetchId.export));
            const response = await Utilities.getFetchWithHeaders(`api/processList/export/${agentGroupName}`, {
                ids: ids.map((k) => k.toString()),
            });
            const fileName = Utilities.parseFileNameFromContentDisposition(response.headers.get("Content-Disposition"));
            download(
                // ACC-8685 Add UTF-8 header to the data. This fixes some encoding issues.
                new Blob(["\ufeff", JSONbig.stringify(response.content as any, null, "\t")]),
                fileName ?? "ProcessExport",
                "application/json; charset=utf-8");
            dispatch(finishListAction(FetchId.export));
            Utilities.toastSuccess(FRENDS.Localizations.Successful);
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
        }
    };
}


export const TOGGLE_VISIBLE_PROCESS_LIST_COLUMN = "TOGGLE_VISIBLE_PROCESS_LIST_COLUMN";
export function toggleVisibleColumn(column: keyof (ProcessWithStatus)): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();
        const isIncomingColumnVisible = state.listState.visibleColumns.includes(column);
        if (!isIncomingColumnVisible && state.listState.visibleColumns.length - 1 === maxVisibleColumns) {
            return;
        }
        const visibleColumns = toggleVisibleColumns(column, state.listState.visibleColumns);
        const currentAgentGroup = getCurrentAgentGroup(state);
        if (_.isEmpty(state.referencedProcessMap) && visibleColumns.some((col) => col === "usedProcessesList" || col === "usedByProcessesList")) {
            dispatch(fetchSubprocessUsageData(currentAgentGroup.environment, currentAgentGroup.name));
        }

        if (visibleColumns.some((col) => col === "logLevel")) {
            dispatch(FetchActions.fetchLogLevelUsageData(currentAgentGroup.environment, currentAgentGroup.name));
        }

        if (visibleColumns.some((col) => col === "logRetention")) {
            dispatch(FetchActions.fetchLogRetentionUsageData(currentAgentGroup.environment, currentAgentGroup.name));
        }


        dispatch({
            type: TOGGLE_VISIBLE_PROCESS_LIST_COLUMN,
            column: column
        });
    };
}

export interface ToggleVisibleProcessListColumn extends Redux.Action {
    column: keyof (ProcessWithStatus);
}

export const CLOSE_CONFIRM_DELETE_DIALOG = "CLOSE_CONFIRM_DELETE_DIALOG";
export const OPEN_CONFIRM_DELETE_DIALOG = "OPEN_CONFIRM_DELETE_DIALOG";

export interface IOpenConfirmDeleteDialogAction extends Redux.Action {
    dialogState: ConfirmDeleteDialogState;
}

export function changeCountsTimeLimitDays(newLimitDays: number): ThunkResult<void, State> {
    return (dispatch, getState) => {
        dispatch({
            type: CHANGE_COUNTS_TIME_LIMIT,
            newLimitDays: newLimitDays
        });
        const state = getState();
        const currentAgentGroupName = state.currentAgentGroupName;
        const currentEnvironmentName = state.agentGroups[currentAgentGroupName].environment;
        // Return for test to work
        return dispatch(FetchActions.fetchCounts(currentEnvironmentName, currentAgentGroupName, newLimitDays));
    };
}

export const CHANGE_COUNTS_TIME_LIMIT = "CHANGE_COUNTS_TIME_LIMIT";
export interface ChangeCountsTimeLimitAction extends Redux.Action {
    newLimitDays: number;
}

const getProcessesGuids = (processIds: number[], processes: Process[]): string[] => processes
    .filter((f) => processIds
        .some((pi) => pi === f.id))
    .map((m) => m.uniqueIdentifier)

export function triggerOpenProcessVariablesEditorModal(processId: number): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        const state = getState();
        const process = state.processes.find(p => p.id === processId);

        if (!process) {
            return;
        }

        dispatch(receiveProcessVariables(process.processVariables));
        dispatch(openProcessVariablesEditorModalAction(processId));
    };
}

export const OPEN_PROCESS_VARIABLES_EDITOR_MODAL = "OPEN_PROCESS_VARIABLES_EDITOR_MODAL";
export interface OpenProcessVariablesEditorModalAction extends Redux.Action {
    processId: number;
}

function openProcessVariablesEditorModalAction(processId: number): OpenProcessVariablesEditorModalAction {
    return {
        type: OPEN_PROCESS_VARIABLES_EDITOR_MODAL,
        processId: processId,
    };
}

export function updateProcessVariables(processId: number | undefined): ThunkResult<void, State> {
    return async (dispatch, getState) => {
        if (!processId) {
            return;
        }
        const state = getState();

        const processVariables = state.processVariablesEditorState.processVariables;
        const agentGroup = state.currentAgentGroupName;

        try {
            await Utilities.postJsonAndParseResponse(`api/process/updateProcessVariables/${agentGroup}/${processId}`, processVariables);
            Utilities.toastSuccess(FRENDS.Localizations.Successful);
            dispatch(closeProcessVariablesEditorModal());
            dispatch(ProcessActions.updateProcessVariables(processId, processVariables, agentGroup));
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
        }
    };
}

export const CLOSE_PROCESS_VARIABLES_EDITOR_MODAL = "CLOSE_PROCESS_VARIABLES_EDITOR_MODAL";
export function closeProcessVariablesEditorModal(): Redux.Action {
    return {
        type: CLOSE_PROCESS_VARIABLES_EDITOR_MODAL,
    };
}

export const OPEN_COLUMN_VISIBILITY_CONTROL = "OPEN_COLUMN_VISIBILITY_CONTROL";
export function openColumnVisibilityControl(): Redux.Action {
    return {
        type: OPEN_COLUMN_VISIBILITY_CONTROL,
    };
}

export const CLOSE_COLUMN_VISIBILITY_CONTROL = "CLOSE_COLUMN_VISIBILITY_CONTROL";
export function closeColumnVisibilityControl(): Redux.Action {
    return {
        type: CLOSE_COLUMN_VISIBILITY_CONTROL,
    };
}

export interface ActionMenuOpenForProcessAction extends Redux.Action {
    processId: number;
}

export const SET_ACTION_MENU_OPEN_FOR_PROCESS = "SET_ACTION_MENU_OPEN_FOR_PROCESS";
export function setActionMenuOpenForProcess(processId: number): ActionMenuOpenForProcessAction {
    return {
        type: SET_ACTION_MENU_OPEN_FOR_PROCESS,
        processId: processId,
    };
}

export const REMOVE_ACTION_MENU_OPEN_FOR_PROCESS = "REMOVE_ACTION_MENU_OPEN_FOR_PROCESS";
export function removeActionMenuOpenForProcess(): Redux.Action {
    return {
        type: REMOVE_ACTION_MENU_OPEN_FOR_PROCESS,
    };
}

export interface SetSelectCreateTemplateFromProcessModalModeAction extends Redux.Action {
    modalProps: TemplateModalProps | null;
}
export const SET_SELECT_CREATE_TEMPLATE_FROM_PROCESS_MODE_MODAL = "SET_SELECT_CREATE_TEMPLATE_FROM_PROCESS_MODE_MODAL";
export function setSelectCreateTemplateFromProcessModeModal(processId: number | null): ThunkResult<void, State> {
    return async (dispatch) => {
        try {
            const existingTemplateRes = await Utilities.getFetchWithoutResultParsing(`api/processList/template/${processId}`, undefined, undefined);
            if (existingTemplateRes.ok) {
                const existingTemplate: ProcessTemplate = await Utilities.parseJsonResponse(existingTemplateRes);
                dispatch({
                    type: SET_SELECT_CREATE_TEMPLATE_FROM_PROCESS_MODE_MODAL,
                    modalProps: {
                        processId,
                        existingTemplate
                    }
                });
            } else if (existingTemplateRes.status === 404) {
                dispatch({
                    type: SET_CREATE_TEMPLATE_FROM_PROCESS_MODAL,
                    modalProps: { processId }
                });
            } else {
                throw new Error(`Failed to fetch Template for Process with id ${processId}`);
            }
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
        }
    }
}

export function unlinkTemplate(processId: number): ThunkResult<void, State> {
    return async (dispatch) => {
        try {
            await Utilities.patchJsonAndParseResponse(`api/processTemplate/unlinkFromTemplate/${processId}`);
            Utilities.toastSuccess("Successfully unlinked Process from Template.")
            dispatch(FetchActions.fetchProcesses());
        } catch (error) {
            dispatch(FetchActions.reportFetchError(error));
        }
    }
}

export interface SetCreateTemplateFromProcessModalAction extends Redux.Action {
    modalProps: TemplateModalProps | null;
}
export const SET_CREATE_TEMPLATE_FROM_PROCESS_MODAL = "SET_CREATE_TEMPLATE_FROM_PROCESS_MODAL";
export function setCreateTemplateFromProcessModal(modalProps: TemplateModalProps | null): SetCreateTemplateFromProcessModalAction {
    return {
        type: SET_CREATE_TEMPLATE_FROM_PROCESS_MODAL,
        modalProps
    };
}